"use client"

import * as React from "react"
import { X, ChevronDown, Search } from "lucide-react"
import { cn } from "@/lib/utils"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import { Input } from "@/components/ui/input"

export interface MultiSelectOption {
  value: string
  label: string
}

interface MultiSelectCheckboxProps {
  options: MultiSelectOption[]
  selectedValues: string[]
  onSelectionChange: (values: string[]) => void
  placeholder?: string
  selectAllLabel?: string
  className?: string
  maxDisplayItems?: number
  searchable?: boolean
  searchPlaceholder?: string
  hideBadge?: boolean
  /** Mostra os nomes selecionados no próprio gatilho (útil junto de `hideBadge`). */
  showLabelsInTrigger?: boolean
}

export function MultiSelectCheckbox({
  options,
  selectedValues,
  onSelectionChange,
  placeholder = "Selecione...",
  selectAllLabel = "SELECIONAR TUDO",
  className,
  maxDisplayItems = 3,
  searchable = false,
  searchPlaceholder = "Buscar...",
  hideBadge = false,
  showLabelsInTrigger = false,
}: MultiSelectCheckboxProps) {
  const [open, setOpen] = React.useState(false)
  const [searchTerm, setSearchTerm] = React.useState("")

  const filteredOptions = searchable && searchTerm
    ? options.filter(opt => opt.label.toLowerCase().includes(searchTerm.toLowerCase()))
    : options

  const isAllSelected = filteredOptions.length > 0 && selectedValues.length === options.length
  const isIndeterminate = selectedValues.length > 0 && selectedValues.length < options.length

  const handleSelectAll = () => {
    if (isAllSelected) {
      onSelectionChange([])
    } else {
      onSelectionChange(options.map(opt => opt.value))
    }
  }

  const handleToggleItem = (value: string) => {
    if (selectedValues.includes(value)) {
      onSelectionChange(selectedValues.filter(v => v !== value))
    } else {
      onSelectionChange([...selectedValues, value])
    }
  }

  const handleRemoveItem = (value: string, e: React.MouseEvent) => {
    e.stopPropagation()
    onSelectionChange(selectedValues.filter(v => v !== value))
  }

  const getSelectedLabels = () => {
    return selectedValues.map(value => {
      const option = options.find(opt => opt.value === value)
      return option?.label || value
    })
  }

  const selectedLabels = getSelectedLabels()

  return (
    <div className={cn("space-y-2", className)}>

      {hideBadge || selectedValues.length === 0 || (
        <div className="flex flex-wrap gap-2">
          {selectedLabels.slice(0, maxDisplayItems).map((label, index) => (
            <Badge
              key={selectedValues[index]}
              variant="secondary"
              className="flex items-center gap-1"
            >
              {label}
              <X
                className="h-3 w-3 cursor-pointer hover:text-red-500"
                onClick={(e) => handleRemoveItem(selectedValues[index], e)}
              />
            </Badge>
          ))}
          {selectedLabels.length > maxDisplayItems && (
            <Badge variant="secondary">
              +{selectedLabels.length - maxDisplayItems} mais
            </Badge>
          )}
        </div>
      )}

      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className="w-full justify-between font-normal bg-background text-foreground border-input hover:bg-accent hover:text-foreground"
          >
            <span
              className={cn(
                "truncate",
                (selectedValues.length === 0 || !showLabelsInTrigger) && "text-muted-foreground"
              )}
            >
              {selectedValues.length === 0
                ? placeholder
                : showLabelsInTrigger
                  ? selectedLabels.join(", ")
                  : `${selectedValues.length} selecionado${selectedValues.length > 1 ? 's' : ''}`}
            </span>
            <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-full min-w-[200px] p-0" align="start">
          {searchable && (
            <div className="p-2 border-b">
              <div className="relative">
                <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder={searchPlaceholder}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-8 h-8"
                />
              </div>
            </div>
          )}
          <div className="max-h-[300px] overflow-y-auto">
            <div
              className="flex items-center space-x-2 px-3 py-2 border-b bg-gray-50 dark:bg-zinc-800 cursor-pointer hover:bg-gray-100 dark:hover:bg-zinc-700"
              onClick={handleSelectAll}
            >
              <Checkbox
                checked={isAllSelected}
                className={cn(
                  isIndeterminate && "data-[state=unchecked]:bg-primary/50 data-[state=unchecked]:border-primary"
                )}
              />
              <span className="text-sm font-medium uppercase">{selectAllLabel}</span>
              {isIndeterminate && (
                <span className="text-xs text-muted-foreground">
                  ({selectedValues.length}/{options.length})
                </span>
              )}
            </div>

            {filteredOptions.map((option) => {
              const isSelected = selectedValues.includes(option.value)
              return (
                <div
                  key={option.value}
                  className={cn(
                    "flex items-center space-x-2 px-3 py-2 cursor-pointer hover:bg-gray-100 dark:hover:bg-zinc-700",
                    isSelected && "bg-primary/5"
                  )}
                  onClick={() => handleToggleItem(option.value)}
                >
                  <Checkbox checked={isSelected} />
                  <span className="text-sm">{option.label}</span>
                </div>
              )
            })}

            {filteredOptions.length === 0 && (
              <div className="px-3 py-4 text-center text-sm text-muted-foreground">
                {searchTerm ? "Nenhum resultado encontrado" : "Nenhuma opção disponível"}
              </div>
            )}
          </div>
        </PopoverContent>
      </Popover>
    </div>
  )
}
